import { XMarkIcon } from '@heroicons/react/24/outline';
import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  file: File;
  onClose: () => void;
  actions?: ReactNode;
}

export function AttachmentPreview({
  file,
  onClose,
  actions,
}: Props): ReactNode {
  const [url] = useState(() => URL.createObjectURL(file));

  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const overlay = createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-6"
      role="dialog"
      aria-modal="true"
      aria-label={`Preview: ${file.name}`}
      onClick={onClose}
    >
      <div
        className="dark:bg-ondark-bg-1 flex h-[85vh] w-[70vw] flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dark:border-ondark-line-2 border-onlight-line-2 flex shrink-0 items-center justify-between border-b px-4 py-3">
          <span className="text-onlight-text-1 dark:text-ondark-text-1 max-w-[80%] truncate text-sm font-medium">
            {file.name}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-onlight-text-2 dark:text-ondark-text-2 dark:hover:bg-ondark-bg-3 hover:bg-onlight-bg-2 rounded p-1"
            aria-label="Close preview"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <iframe
          src={
            file.type === 'application/pdf'
              ? `${url}#toolbar=0&navpanes=0&scrollbar=0`
              : url
          }
          title={file.name}
          className="flex-1 bg-white"
        />
        {actions && (
          <div className="dark:border-ondark-line-2 border-onlight-line-2 flex shrink-0 items-center gap-2 border-t px-4 py-3">
            {actions}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
  return overlay as unknown as ReactNode;
}

export default AttachmentPreview;
